import React, { forwardRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import "react-datepicker/dist/react-datepicker.css";
import "./DateTimePicker.css";

function pad(n) {
  return String(n).padStart(2, "0");
}

// Mirrors the native <input type="datetime-local"> value shape
// ("YYYY-MM-DDTHH:mm") so it's a drop-in replacement for existing form state.
export function toLocalString(date) {
  if (!date) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalString(value) {
  if (!value) return null;
  const [datePart, timePart = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

// Shifts a "YYYY-MM-DDTHH:mm" value by the given number of hours, returning
// the same string shape so callers can feed it straight back into a field.
export function addHoursToLocalString(value, hours) {
  const date = fromLocalString(value);
  if (!date) return "";
  date.setHours(date.getHours() + hours);
  return toLocalString(date);
}

const TriggerButton = forwardRef(
  ({ value, onClick, placeholder, disabled, hasError, className }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      className={`${className} flex items-center justify-between gap-2 text-left ${
        hasError ? "ring-2 ring-red-400" : ""
      }`}
    >
      <span className={value ? "text-gray-900" : "text-gray-400"}>
        {value || placeholder}
      </span>
      <CalendarDaysIcon className="h-5 w-5 text-gray-400 shrink-0" />
    </button>
  )
);

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date & time",
  className,
  disabled,
  hasError,
  minDate,
}) {
  return (
    <DatePicker
      selected={fromLocalString(value)}
      onChange={(date) => onChange(toLocalString(date))}
      showTimeSelect
      timeIntervals={5}
      minDate={minDate}
      dateFormat="MMM d, yyyy h:mm aa"
      disabled={disabled}
      wrapperClassName="app-datepicker-wrapper"
      popperClassName="app-datepicker-popper"
      // Renders the calendar/time popup into document.body instead of right
      // after the input. Without this, any ancestor with `overflow-hidden`
      // (e.g. a rounded modal card) clips the bottom of the popup — the
      // popper's floating position math is unaffected, only where its DOM
      // node lives.
      popperContainer={({ children }) => createPortal(children, document.body)}
      customInput={
        <TriggerButton
          placeholder={placeholder}
          className={className}
          hasError={hasError}
        />
      }
    />
  );
}
